import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowRight,
  BarChart3,
  Calculator,
  CheckCircle2,
  Clock3,
  Cog,
  Database,
  Factory,
  GitBranch,
  Layers3,
  Menu,
  MessageCircle,
  PackageCheck,
  RefreshCw,
  ShieldCheck,
  ShoppingCart,
  Store,
  Utensils,
  Warehouse,
  X,
} from 'lucide-react';

const INDUSTRY_IMAGES = {
  fnb: 'https://d2xsxph8kpxj0f.cloudfront.net/310519663498110137/o9P47bNmwAsQCaRDZVgb7W/industry-fnb-jqD9VMweHkf5fGyDYGVp3W.webp',
  fashion:
    'https://d2xsxph8kpxj0f.cloudfront.net/310519663498110137/o9P47bNmwAsQCaRDZVgb7W/industry-fashion-fiVfpEyyoT863QRrbZkyPa.webp',
  retail:
    'https://d2xsxph8kpxj0f.cloudfront.net/310519663498110137/o9P47bNmwAsQCaRDZVgb7W/industry-retail-hjb5MehxfuY4pBnWY5Ki5H.webp',
  manufacturing:
    'https://d2xsxph8kpxj0f.cloudfront.net/310519663498110137/o9P47bNmwAsQCaRDZVgb7W/industry-manufacturing-HUUEzCDH6DGz4NwmMTovR5.webp',
};

const navItems = [
  { label: 'Fitur', href: '#fitur' },
  { label: 'Industri', href: '#industri' },
  { label: 'Harga', href: '#harga' },
  { label: 'FAQ', href: '#faq' },
];

const STOCKARA_WHATSAPP_URL =
  'https://wa.me/6285212546871?text=Halo%20Stockara%2C%20saya%20ingin%20coba%20gratis%2014%20hari%20dan%20aktivasi%20akun%20bisnis.';

const stats = [
  { value: '8+ modul', label: 'dalam satu platform' },
  { value: '3 marketplace', label: 'sinkron real-time' },
  { value: '4 industri', label: 'template siap pakai' },
];

const heroModules = [
  { icon: ShoppingCart, label: 'Kasir & Penjualan' },
  { icon: Warehouse, label: 'Stok & Marketplace' },
  { icon: Calculator, label: 'Keuangan' },
  { icon: Cog, label: 'Manufaktur' },
  { icon: MessageCircle, label: 'AI CS WhatsApp' },
  { icon: BarChart3, label: 'AI Assistant' },
];

const differentiators = [
  {
    icon: Layers3,
    title: 'Bukan cuma kasir yang dikasih embel-embel AI',
    text: 'Banyak aplikasi kasir nambahin chatbot lalu disebut "AI". Di Stockara, AI baca data stok, keuangan, dan penjualan yang sama dengan yang dipakai tim kamu kerja setiap hari, bukan chatbot terpisah yang buta data.',
  },
  {
    icon: GitBranch,
    title: 'Bukan cuma pembukuan yang kebetulan bisa jualan',
    text: 'Software akuntansi murah biasanya berhenti di jurnal dan laporan. Stockara nyambungin transaksi kasir, stok, sampai neraca dan laba-rugi dalam satu alur, tanpa input dobel atau rekonsiliasi manual.',
  },
  {
    icon: Factory,
    title: 'Satu-satunya yang nyampe ke lantai produksi',
    text: 'Mau bisnismu jualan retail sekaligus produksi sendiri? Stockara punya BOM, MRP, dan HPP bawaan, bukan modul terpisah yang harus dibeli dari vendor lain dan diintegrasikan manual.',
  },
];

const stockModule = {
  id: 'stok-marketplace',
  tone: 'base',
  eyebrow: 'Stok & Marketplace',
  icon: Warehouse,
  headline: 'Stok yang selalu sama, di toko fisik maupun semua marketplace.',
  copy: 'Kelola multi-gudang, varian produk (size/warna), dan stok per lokasi dari satu dashboard. Begitu ada penjualan di Shopee, TikTok Shop, atau Tokopedia, stok di semua channel lain ikut ke-update otomatis, tidak perlu update manual satu-satu dan risiko oversell.',
  features: [
    'Sinkron order & stok real-time ke Shopee, TikTok Shop, Tokopedia, dan website',
    'Multi-gudang dan multi-lokasi dengan transfer stok antar cabang',
    'Varian produk per size/warna dengan SKU dan matrix stok sendiri',
    'Stock opname, reorder point, dan riwayat pergerakan stok lengkap',
  ],
};

const financeModule = {
  id: 'keuangan',
  tone: 'alt',
  eyebrow: 'Keuangan',
  icon: Calculator,
  headline: 'Laporan keuangan yang beres sendiri, bukan PR akhir bulan.',
  copy: 'Setiap transaksi kasir, pembelian, dan pengeluaran otomatis masuk ke jurnal. Owner tinggal buka Neraca dan Laba-Rugi kapan saja tanpa nunggu tim akunting rekap manual dari Excel.',
  features: [
    'Neraca dan Laba-Rugi otomatis dari transaksi harian',
    'Jurnal umum, chart of accounts, dan cashflow real-time',
    'Piutang dan hutang dengan status jatuh tempo',
    'Aset tetap dan depresiasi, plus pajak dasar tersedia',
  ],
};

const manufacturingModule = {
  id: 'manufaktur',
  tone: 'base',
  eyebrow: 'Manufaktur & Produksi',
  icon: Cog,
  headline: 'Dari bahan baku sampai HPP, terhitung otomatis.',
  copy: 'Untuk bisnis yang produksi sendiri: susun Bill of Material, biar sistem yang hitung kebutuhan bahan dan bikinkan draft PO ke supplier. HPP per produk terhitung otomatis dari resep dan biaya aktual, bukan kira-kira.',
  features: [
    'BOM (Bill of Material) multi-level per produk',
    'MRP untuk hitung kebutuhan bahan otomatis dan generate draft PO',
    'HPP/COGS otomatis dari resep dan biaya produksi aktual',
    'Quality control dan pencatatan reject/scrap per batch produksi',
  ],
};

const industries = [
  {
    id: 'fnb',
    title: 'FnB & Restoran',
    text: 'Untuk restoran, kafe, cloud kitchen, katering, dan bisnis makanan yang butuh kontrol menu, bahan baku, dan pesanan.',
    image: INDUSTRY_IMAGES.fnb,
    icon: Utensils,
    headline: 'Template FnB dibuat supaya dapur, kasir, dan owner tidak jalan sendiri-sendiri.',
    copy:
      'Customer pesan menu, kasir catat transaksi, dapur tahu item yang harus disiapkan, dan owner tetap bisa melihat bahan baku serta laporan POS harian.',
    features: [
      'Menu, varian, paket, promo, dan seasonal menu',
      'Resep, bahan baku, estimasi kebutuhan harian, expiry, dan waste tracking',
      'Table layout, reservasi, QR order, split bill, dan laporan POS dasar',
      'GoFood/GrabFood sync, delivery order, dan preorder katering',
    ],
  },
  {
    id: 'fashion',
    title: 'Fashion',
    text: 'Untuk brand fashion, butik, konveksi kecil, dan reseller yang butuh stok per size, warna, SKU, dan channel.',
    image: INDUSTRY_IMAGES.fashion,
    icon: Layers3,
    headline: 'Template Fashion fokus ke varian, stok per channel, dan kontrol produksi.',
    copy:
      'Setiap warna dan size punya SKU sendiri. Owner bisa lihat stok ready, preorder, retur, dan channel mana yang paling cepat bergerak.',
    features: [
      'Variant matrix, SKU per varian, stok per size/warna, dan hangtag print',
      'BOM fashion, work order, cutting order, QC checklist, dan defect tracking',
      'Preorder, katalog digital, reseller, konsinyasi, dan sales rep tracking',
      'Marketplace sync, return customer/supplier, courier, dan multi-gudang',
    ],
  },
  {
    id: 'retail',
    title: 'Retail',
    text: 'Untuk toko fisik dan online yang perlu POS cepat, stok rapi, pelanggan kebaca, dan promo lebih fleksibel.',
    image: INDUSTRY_IMAGES.retail,
    icon: Store,
    headline: 'Template Retail dibuat untuk transaksi harian yang cepat dan laporan yang gampang dibaca.',
    copy:
      'Kasir bisa fokus melayani pembeli, stok otomatis ikut bergerak, pelanggan terekam, dan owner punya ringkasan toko tanpa rekap manual.',
    features: [
      'POS retail, multi-kasir, cashier closing, refund/exchange, dan price tag',
      'Customer database, membership, loyalty, purchase history, dan member promo',
      'Stock opname, reorder point, multi-location stock, dan multi-store',
      'Online order, marketplace retail, promo rules, dan omnichannel stock sync',
    ],
  },
  {
    id: 'manufacturing',
    title: 'Manufaktur',
    text: 'Untuk produksi, workshop, dan pabrik kecil-menengah yang butuh work order, BOM, QC, dan kontrol bahan.',
    image: INDUSTRY_IMAGES.manufacturing,
    icon: Factory,
    headline: 'Template Manufaktur membantu produksi lebih terukur dari order sampai invoice.',
    copy:
      'Tim bisa lihat kebutuhan bahan, jadwal kerja, status produksi, QC, scrap/reject, sampai dokumen tagihan yang perlu dikirim.',
    features: [
      'Quotation, sales order B2B, contract, work order, dan scheduling',
      'Multi-level BOM, material requisition, purchasing, dan goods receipt',
      'Quality control, scrap/reject tracking, rework, dan compliance docs',
      'Machine maintenance, downtime, actual vs target, invoice, dan faktur',
    ],
  },
];

const workflow = [
  {
    icon: Database,
    title: 'Masukkan data awal',
    text: 'Mulai dari produk, pelanggan, cabang, bahan baku, atau supplier. Bisa manual atau lewat bulk import.',
  },
  {
    icon: Clock3,
    title: 'Tim mulai transaksi',
    text: 'Kasir, gudang, admin, atau owner memakai menu sesuai role. Tidak semua orang perlu melihat semuanya.',
  },
  {
    icon: BarChart3,
    title: 'Owner melihat ringkasan',
    text: 'Dashboard menampilkan kondisi bisnis hari ini: stok, penjualan, cashflow, dan pekerjaan yang perlu ditangani.',
  },
];

const aiCards = [
  {
    icon: MessageCircle,
    title: 'AI CS WhatsApp',
    text: 'Bantu jawab chat customer, cek stok, dan arahkan pembelian tanpa owner harus standby terus.',
  },
  {
    icon: BarChart3,
    title: 'AI Assistant Owner',
    text: 'Ringkas penjualan, stok menipis, cashflow, dan pekerjaan penting dalam bahasa yang gampang dipahami.',
  },
  {
    icon: PackageCheck,
    title: 'Rekomendasi stok',
    text: 'Bantu membaca produk yang cepat habis, item lambat bergerak, dan prioritas restock harian.',
  },
];

const industryChatScripts = {
  FnB: [
    {
      type: 'ai',
      text: 'Halo Kak, selamat datang di Kopi Sore. Lagi mau pesan kopi atau mau cek menu yang ready hari ini?',
      delay: 1400,
      showTyping: true,
    },
    { type: 'user', text: 'Kopi Gula Aren ready?', delay: 2200 },
    {
      type: 'ai',
      text: 'Ready Kak. Kopi Gula Aren Signature masih tersedia 18 cup. Rasanya creamy, pakai gula aren, dan harganya Rp 22.000.',
      image: '/stockara-demo-fnb.jpg',
      productName: 'Kopi Gula Aren Signature',
      price: 'Rp 22.000',
      stock: 'Ready: 18 cup',
      delay: 2300,
      showTyping: true,
    },
    { type: 'user', text: 'Pesan 2 ya, kirim ke Meja 5', delay: 2400 },
    {
      type: 'ai',
      text: 'Siap Kak, aku buatkan pesanan 2 Kopi Gula Aren untuk Meja 5. Totalnya Rp 44.000, dan pesanan sudah masuk ke bar.',
      delay: 2400,
      showTyping: true,
    },
    { type: 'user', text: 'Aku transfer sekarang ya', delay: 2300 },
    {
      type: 'user',
      text: 'Ini bukti transfernya Kak.',
      proof: {
        bank: 'BCA',
        amount: 'Rp 44.000',
        ref: 'TRX-882104',
        image: '/stockara-proof-fnb.jpg',
      },
      delay: 2200,
    },
    {
      type: 'ai',
      text: 'Makasih Kak, pembayarannya sudah terbaca. Ini invoice pesanannya ya, nanti tinggal tunjukkan ke kasir kalau diminta.',
      invoice: {
        id: 'INV-FNB-1028',
        image: '/stockara-invoice-fnb.jpg',
        business: 'Kopi Sore',
        date: '14 Jul 2026, 15.42',
        customer: 'Meja 5',
        items: [{ name: 'Kopi Gula Aren Signature', qty: 2, price: 22000 }],
        total: 'Rp 44.000',
        status: 'Lunas',
      },
      delay: 2600,
      showTyping: true,
    },
  ],
  Retail: [
    {
      type: 'ai',
      text: 'Halo Kak, selamat datang di Toko Berkah Jaya. Mau cek stok barang apa hari ini?',
      delay: 1400,
      showTyping: true,
    },
    { type: 'user', text: 'Minyak goreng kelapa 2L ada stok?', delay: 2200 },
    {
      type: 'ai',
      text: 'Ada Kak. Minyak Goreng Kelapa 2L masih ready 45 botol di rak utama. Harga per botol Rp 34.500.',
      image: '/stockara-demo-retail.jpg',
      productName: 'Minyak Goreng Kelapa 2L',
      price: 'Rp 34.500',
      stock: 'Ready: 45 botol',
      delay: 2300,
      showTyping: true,
    },
    { type: 'user', text: 'Ambil 3 botol, aku bayar transfer.', delay: 2400 },
    {
      type: 'user',
      text: 'Sudah transfer ke rekening toko.',
      proof: { bank: 'Mandiri', amount: 'Rp 103.500', ref: 'TRX-441920', image: '/stockara-proof-retail.jpg' },
      delay: 2200,
    },
    {
      type: 'ai',
      text: 'Baik Kak, transfernya sudah diterima. Invoice sudah dibuat dan stoknya otomatis berkurang 3 botol.',
      invoice: {
        id: 'INV-RTL-2210',
        image: '/stockara-invoice-retail.jpg',
        business: 'Toko Berkah Jaya',
        date: '14 Jul 2026, 10.15',
        customer: 'Customer WhatsApp',
        items: [{ name: 'Minyak Goreng Kelapa 2L', qty: 3, price: 34500 }],
        total: 'Rp 103.500',
        status: 'Lunas',
      },
      delay: 2600,
      showTyping: true,
    },
  ],
  Fashion: [
    {
      type: 'ai',
      text: 'Halo Sis, selamat datang di ModeStyle. Mau cek koleksi, warna, atau ukuran yang masih ready?',
      delay: 1400,
      showTyping: true,
    },
    { type: 'user', text: 'Kemeja Linen Putih ukuran XL ready ga?', delay: 2200 },
    {
      type: 'ai',
      text: 'Ready Sis. Kemeja Linen Putih ukuran XL masih ada 3 pcs. Bahannya adem dan cocok buat daily outfit.',
      image: '/stockara-demo-fashion.jpg',
      productName: 'Kemeja Linen Premium White',
      price: 'Rp 185.000',
      stock: 'Ready: 3 pcs XL',
      delay: 2300,
      showTyping: true,
    },
    { type: 'user', text: 'Bisa tolong simpan 1? Aku otw toko sekarang', delay: 2400 },
    {
      type: 'ai',
      text: 'Bisa Sis. Aku hold 1 pcs ukuran XL putih selama 1 jam ya, jadi stoknya tidak terjual ke customer lain dulu.',
      delay: 2400,
      showTyping: true,
    },
    { type: 'user', text: 'Aku transfer full aja ya biar aman.', delay: 2300 },
    {
      type: 'user',
      text: 'Bukti transfer sudah masuk.',
      proof: { bank: 'BRI', amount: 'Rp 185.000', ref: 'TRX-778310', image: '/stockara-proof-fashion.jpg' },
      delay: 2200,
    },
    {
      type: 'ai',
      text: 'Pembayaran sudah masuk. Invoice sudah dibuat, dan status reservasi produk sudah aktif.',
      invoice: {
        id: 'INV-FSH-0917',
        image: '/stockara-invoice-fashion.jpg',
        business: 'ModeStyle',
        date: '14 Jul 2026, 13.08',
        customer: 'Customer ModeStyle',
        items: [{ name: 'Kemeja Linen Premium White - XL', qty: 1, price: 185000 }],
        total: 'Rp 185.000',
        status: 'Reserved & Lunas',
      },
      delay: 2600,
      showTyping: true,
    },
  ],
  Manufaktur: [
    {
      type: 'ai',
      text: 'Halo, saya bantu cek data produksi dan stok bahan baku. Mau mulai dari stok, order produksi, atau kebutuhan restock?',
      delay: 1400,
      showTyping: true,
    },
    { type: 'user', text: 'Cek stok kain drill biru navy di gudang B', delay: 2200 },
    {
      type: 'ai',
      text: 'Stok kain drill biru navy di Gudang B tersisa 12 roll atau sekitar 600 meter. Batas minimum produksi 900 meter, jadi sebaiknya segera restock.',
      image: '/stockara-demo-manufaktur.jpg',
      productName: 'Kain Drill Biru Navy',
      price: '600 meter tersisa',
      stock: 'Min. produksi: 900 meter',
      delay: 2400,
      showTyping: true,
    },
    { type: 'user', text: 'Buat permintaan restock ke supplier', delay: 2400 },
    {
      type: 'ai',
      text: 'Siap, draft Purchase Order untuk 20 roll kain drill sudah dibuat. Statusnya menunggu persetujuan manajer gudang.',
      delay: 2500,
      showTyping: true,
    },
    { type: 'user', text: 'Supplier minta DP, ini bukti transfernya.', proof: { bank: 'BNI', amount: 'Rp 4.000.000', ref: 'TRX-550219', image: '/stockara-proof-manufaktur.jpg' }, delay: 2200 },
    {
      type: 'ai',
      text: 'Bukti DP sudah tercatat. Invoice internal dan draft PO sudah diperbarui untuk proses approval.',
      invoice: {
        id: 'INV-MFG-3302',
        image: '/stockara-invoice-manufaktur.jpg',
        business: 'Divisi Produksi',
        date: '14 Jul 2026, 09.21',
        customer: 'PO Supplier Tekstil',
        items: [{ name: 'DP 20 roll Kain Drill Biru Navy', qty: 1, price: 4000000 }],
        total: 'Rp 4.000.000',
        status: 'DP tercatat',
      },
      delay: 2600,
      showTyping: true,
    },
  ],
};

const plans = [
  {
    name: 'Gratis',
    price: 'Rp 0',
    period: '14 hari trial',
    description: 'Coba dulu sampai yakin.',
    cta: 'Mulai gratis',
    features: ['Semua fitur aktif saat trial', 'Inventori dan penjualan dasar', 'Laporan awal', 'Tanpa kartu kredit'],
  },
  {
    name: 'Standard',
    price: 'Rp 99.000',
    period: '/bulan',
    description: 'Untuk toko yang mulai rapiin operasional.',
    cta: 'Pilih Standard',
    features: ['3 akun pengguna', '500 SKU produk', '1 integrasi marketplace', 'Support WhatsApp'],
  },
  {
    name: 'Plus',
    price: 'Rp 199.000',
    period: '/bulan',
    description: 'Untuk bisnis yang transaksinya sudah ramai.',
    cta: 'Pilih Plus',
    highlighted: true,
    features: ['10 akun pengguna', '2.000 SKU produk', 'Multi-gudang', 'Bulk Import Excel', 'Generate API Key'],
  },
  {
    name: 'Max',
    price: 'Rp 270.000',
    period: '/bulan',
    description: 'Untuk owner yang butuh kontrol penuh.',
    cta: 'Pilih Max',
    features: ['Pengguna tak terbatas', 'SKU tak terbatas', 'AI Insights', 'Automation Agent', 'Priority support'],
  },
];

const faqs = [
  {
    question: 'Stockara cocok untuk bisnis apa?',
    answer:
      'Cocok untuk bisnis yang punya stok, transaksi, pelanggan, atau proses produksi. Saat setup, menu bisa mengikuti industri seperti retail, FnB, fashion, atau manufaktur.',
  },
  {
    question: 'Apakah harus punya tim IT?',
    answer:
      'Tidak. Stockara dibuat untuk owner dan tim operasional. Kalau datanya sudah siap, setup awal bisa dilakukan bertahap tanpa proses teknis yang rumit.',
  },
  {
    question: 'Kalau bisnis saya punya beberapa cabang?',
    answer:
      'Bisa. Stockara mendukung multi-toko, multi-gudang, role akses, dan laporan yang memisahkan performa per lokasi.',
  },
  {
    question: 'Data trial akan hilang setelah 14 hari?',
    answer:
      'Tidak langsung hilang. Data tetap disimpan agar kamu bisa lanjut ke paket yang sesuai tanpa mengulang input dari nol.',
  },
  {
    question: 'Apakah stok kesinkron otomatis ke Shopee, TikTok Shop, dan Tokopedia?',
    answer:
      'Ya. Begitu ada order masuk atau stok berubah di salah satu channel, Stockara update stok ke semua channel lain secara real-time, jadi risiko oversell jauh lebih kecil.',
  },
  {
    question: 'Apakah ada laporan keuangan seperti neraca dan laba-rugi?',
    answer:
      'Ada. Neraca, laba-rugi, cashflow, dan jurnal umum otomatis terbentuk dari transaksi harian, bukan modul terpisah yang harus diinput ulang manual.',
  },
  {
    question: 'Stockara bisa dipakai untuk bisnis yang produksi sendiri (manufaktur)?',
    answer:
      'Bisa. Ada modul BOM (Bill of Material), MRP untuk hitung kebutuhan bahan otomatis, HPP per produk, dan quality control, cocok untuk workshop atau pabrik kecil-menengah.',
  },
];

function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header className={`sl-header ${scrolled ? 'is-scrolled' : ''}`}>
      <div className="sl-nav-shell">
        <button
          className="sl-menu-btn"
          type="button"
          aria-label={open ? 'Tutup menu' : 'Buka menu'}
          onClick={() => setOpen((value) => !value)}
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>

        <a className="sl-logo-link" href="/" aria-label="Stockara">
          <img src="/logo-stockara.png" alt="Stockara" className="sl-logo-img" />
        </a>

        <nav className="sl-nav-links" aria-label="Navigasi utama">
          {navItems.map((item) => (
            <a key={item.href} href={item.href}>
              {item.label}
            </a>
          ))}
        </nav>

        <div className="sl-nav-actions">
          <a className="sl-login-link" href="/login">
            Masuk
          </a>
          <a className="sl-btn sl-btn-primary sl-btn-small" href={STOCKARA_WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
            Coba gratis 14 hari
          </a>
        </div>
      </div>

      {open && (
        <div className="sl-mobile-panel">
          {navItems.map((item) => (
            <a key={item.href} href={item.href} onClick={() => setOpen(false)}>
              {item.label}
            </a>
          ))}
          <a href="/login" onClick={() => setOpen(false)}>
            Masuk
          </a>
          <a className="sl-mobile-cta" href={STOCKARA_WHATSAPP_URL} target="_blank" rel="noopener noreferrer" onClick={() => setOpen(false)}>
            Coba gratis 14 hari
          </a>
        </div>
      )}
    </header>
  );
}

function SectionLabel({ children }) {
  return <p className="sl-section-label">{children}</p>;
}

function FeatureCard({ feature }) {
  const Icon = feature.icon;
  return (
    <article className="sl-feature-card">
      <div className="sl-feature-icon">
        <Icon size={22} />
      </div>
      <h3>{feature.title}</h3>
      <p>{feature.text}</p>
    </article>
  );
}

function IndustryCard({ item, active, onSelect }) {
  const Icon = item.icon;
  return (
    <button
      type="button"
      className={`sl-industry-card ${active ? 'is-active' : ''}`}
      onClick={() => onSelect(item.id)}
      aria-pressed={active}
    >
      <div className="sl-industry-media">
        <img src={item.image} alt={item.title} loading="lazy" />
      </div>
      <div className="sl-industry-body">
        <div className="sl-industry-icon">
          <Icon size={19} />
        </div>
        <h3>{item.title}</h3>
        <p>{item.text}</p>
      </div>
      <span className="sl-industry-action">{active ? 'Sedang dibuka' : 'Lihat template'}</span>
    </button>
  );
}

function IndustryDetail({ industry }) {
  const Icon = industry.icon;
  return (
    <article className="sl-industry-detail">
      <div className="sl-industry-detail-head">
        <div className="sl-industry-detail-icon">
          <Icon size={24} />
        </div>
        <div>
          <p>Template aktif</p>
          <h3>{industry.title}</h3>
        </div>
      </div>
      <div className="sl-industry-detail-copy">
        <h4>{industry.headline}</h4>
        <p>{industry.copy}</p>
      </div>
      <div className="sl-template-feature-grid">
        {industry.features.map((feature) => (
          <div className="sl-template-feature" key={feature}>
            <CheckCircle2 size={18} />
            <span>{feature}</span>
          </div>
        ))}
      </div>
      <a className="sl-btn sl-btn-primary sl-template-cta" href={STOCKARA_WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
        Coba template {industry.title}
        <ArrowRight size={17} />
      </a>
    </article>
  );
}

function ModuleSection({ module }) {
  const Icon = module.icon;
  return (
    <section id={module.id} className={`sl-section sl-module-section sl-module-${module.tone || 'base'}`}>
      <div className="sl-container">
        <article className="sl-industry-detail sl-module-detail">
          <div className="sl-industry-detail-head">
            <div className="sl-industry-detail-icon">
              <Icon size={24} />
            </div>
            <div>
              <p>{module.eyebrow}</p>
              <h3>{module.headline}</h3>
            </div>
          </div>
          <div className="sl-industry-detail-copy">
            <p>{module.copy}</p>
          </div>
          <div className="sl-template-feature-grid">
            {module.features.map((feature) => (
              <div className="sl-template-feature" key={feature}>
                <CheckCircle2 size={18} />
                <span>{feature}</span>
              </div>
            ))}
          </div>
          <a className="sl-btn sl-btn-primary sl-template-cta" href={STOCKARA_WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
            Coba gratis 14 hari
            <ArrowRight size={17} />
          </a>
        </article>
      </div>
    </section>
  );
}

function HeroModuleGrid() {
  return (
    <div className="sl-hero-modules">
      {heroModules.map((item) => {
        const Icon = item.icon;
        return (
          <div className="sl-hero-module-tile" key={item.label}>
            <div className="sl-hero-module-icon">
              <Icon size={20} />
            </div>
            <span>{item.label}</span>
          </div>
        );
      })}
    </div>
  );
}

function PricingCard({ plan }) {
  return (
    <article className={`sl-price-card ${plan.highlighted ? 'is-highlighted' : ''}`}>
      {plan.highlighted && <div className="sl-price-badge">Paling pas untuk growth</div>}
      <div className="sl-price-head">
        <h3>{plan.name}</h3>
        <p>{plan.description}</p>
      </div>
      <div className="sl-price-row">
        <span className="sl-price-value">{plan.price}</span>
        <span className="sl-price-period">{plan.period}</span>
      </div>
      <a className={`sl-btn ${plan.highlighted ? 'sl-btn-primary' : 'sl-btn-soft'} sl-price-cta`} href={STOCKARA_WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
        {plan.cta}
      </a>
      <ul>
        {plan.features.map((feature) => (
          <li key={feature}>
            <CheckCircle2 size={17} />
            <span>{feature}</span>
          </li>
        ))}
      </ul>
    </article>
  );
}

function FaqItem({ item, index, openIndex, setOpenIndex }) {
  const isOpen = openIndex === index;
  return (
    <div className="sl-faq-item">
      <button type="button" onClick={() => setOpenIndex(isOpen ? null : index)}>
        <span>{item.question}</span>
        <span className="sl-faq-toggle">{isOpen ? '-' : '+'}</span>
      </button>
      {isOpen && <p>{item.answer}</p>}
    </div>
  );
}

// Durasi "mengetik" mengikuti panjang pesan (bukan angka tetap) supaya kerasa kayak orang beneran ngetik,
// bukan animasi presisi. Dikasih jitter kecil di jadwal tiap pesan biar ritmenya gak metronomik.
const typingDurationFor = (message) => {
  const len = (message.text || '').length;
  if (message.type === 'user') {
    return Math.min(1200, Math.max(450, len * 26));
  }
  return Math.min(2200, Math.max(750, len * 22));
};
const withJitter = (base) => Math.max(300, base + Math.floor(Math.random() * 500) - 150);
const LOOP_READ_PAUSE = 3600; // jeda "baca" sebelum lanjut otomatis ke industri berikutnya

function AiVideoScene() {
  const industryNames = Object.keys(industryChatScripts);
  const [activeIndustry, setActiveIndustry] = useState(industryNames[0]);
  const [messages, setMessages] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const [typingSide, setTypingSide] = useState('ai');
  const [inputValue, setInputValue] = useState('');
  const [runId, setRunId] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const scrollRef = useRef(null);
  const frameRef = useRef(null);
  const userInteractedRef = useRef(false);

  // Scroll-trigger: animasi cuma mulai begitu widget ini benar-benar masuk layar pengunjung,
  // dan mulai dari awal lagi tiap kali ke-scroll masuk (bukan langsung autoplay pas halaman dimuat,
  // yang bikin animasi sudah beku duluan sebelum sempat kelihatan).
  useEffect(() => {
    const el = frameRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      setIsVisible(true);
      return undefined;
    }
    // Widget ini ada di hero, jadi sering sudah kelihatan penuh saat halaman pertama dimuat
    // (tidak nunggu user scroll). Cek visibilitas sinkron dulu di sini supaya tidak semata
    // bergantung pada initial callback IntersectionObserver, yang di beberapa kondisi render
    // bisa telat/tidak terpicu selama widget memang tidak pernah berpindah status intersect lagi.
    const rect = el.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const visibleHeight = Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0);
    if (rect.height > 0 && visibleHeight / rect.height >= 0.35) {
      setIsVisible(true);
      setRunId((value) => value + 1);
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
        if (entry.isIntersecting) {
          setRunId((value) => value + 1);
        }
      },
      { threshold: 0.35 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) return undefined;
    const timers = [];
    let elapsed = 0;
    setMessages([]);
    setIsTyping(false);
    userInteractedRef.current = false;

    const script = industryChatScripts[activeIndustry];
    script.forEach((message, index) => {
      elapsed = withJitter(elapsed + (message.delay || 1600));
      const shouldType = message.type === 'user' || message.showTyping;
      timers.push(
        setTimeout(() => {
          if (shouldType) {
            setTypingSide(message.type === 'user' ? 'user' : 'ai');
            setIsTyping(true);
            timers.push(
              setTimeout(() => {
                setIsTyping(false);
                setMessages((prev) => [...prev, { ...message, id: `${activeIndustry}-${runId}-${index}` }]);
              }, typingDurationFor(message))
            );
          } else {
            setMessages((prev) => [...prev, { ...message, id: `${activeIndustry}-${runId}-${index}` }]);
          }
        }, elapsed)
      );
    });

    const lastMessage = script[script.length - 1];
    const totalDuration = elapsed + (lastMessage ? typingDurationFor(lastMessage) : 0) + LOOP_READ_PAUSE;
    timers.push(
      setTimeout(() => {
        if (userInteractedRef.current) return;
        const currentIndex = industryNames.indexOf(activeIndustry);
        setActiveIndustry(industryNames[(currentIndex + 1) % industryNames.length]);
      }, totalDuration)
    );

    return () => timers.forEach((timer) => clearTimeout(timer));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIndustry, runId, isVisible]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isTyping]);

  const resetDemo = () => {
    setRunId((value) => value + 1);
  };

  const sendManualMessage = () => {
    const text = inputValue.trim();
    if (!text) return;
    userInteractedRef.current = true;
    const userMessage = { id: `manual-user-${Date.now()}`, type: 'user', text };
    setInputValue('');
    setMessages((prev) => [...prev, userMessage]);
    setTypingSide('ai');
    setIsTyping(true);
    setTimeout(() => {
      setIsTyping(false);
      setMessages((prev) => [
        ...prev,
        {
          id: `manual-ai-${Date.now()}`,
          type: 'ai',
          text: 'Siap Kak, pesannya sudah masuk. Di versi live, jawaban AI akan mengikuti data stok, transaksi, dan invoice toko secara real-time.',
        },
      ]);
    }, 1500);
  };

  const handleInputKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      sendManualMessage();
    }
  };

  return (
    <div className="sl-chat-demo-frame" ref={frameRef}>
      <div className="sl-chat-demo-toolbar">
        <div>
          <img src="/stockara-ai-icon.png" alt="" className="sl-toolbar-logo" />
          <span>AI Customer Service</span>
        </div>
        <button type="button" onClick={resetDemo} className="sl-chat-reset" aria-label="Reset demo chat">
          <RefreshCw size={14} />
          Reset
        </button>
      </div>
      <div className="sl-chat-demo-body">
        <div className="sl-industry-tabs" role="tablist" aria-label="Pilih industri demo chat">
          {industryNames.map((industry) => (
            <button
              key={industry}
              type="button"
              role="tab"
              aria-selected={activeIndustry === industry}
              className={activeIndustry === industry ? 'is-active' : ''}
              onClick={() => setActiveIndustry(industry)}
            >
              {industry}
            </button>
          ))}
        </div>

        <div className="sl-chat-phone">
          <div className="sl-chat-header">
            <div className="sl-chat-avatar">
              <img src="/stockara-ai-icon.png" alt="" />
            </div>
            <div>
              <strong>Stockara AI</strong>
              <span>{activeIndustry} customer service aktif</span>
            </div>
          </div>

          <div className="sl-chat-messages" ref={scrollRef}>
            {messages.map((message) => (
              <ChatMessage key={message.id} message={message} />
            ))}
            {isTyping && <TypingIndicator side={typingSide} />}
          </div>

          <form
            className="sl-chat-input"
            onSubmit={(event) => {
              event.preventDefault();
              sendManualMessage();
            }}
          >
            <input
              value={inputValue}
              onChange={(event) => setInputValue(event.target.value)}
              onKeyDown={handleInputKeyDown}
              placeholder="Tanya stok atau buat pesanan..."
              aria-label="Ketik pesan demo AI Stockara"
            />
            <button type="submit" aria-label="Simulasi kirim pesan">
              Kirim
            </button>
          </form>
        </div>
      </div>
      <div className="sl-video-footer">
        <span>AI CS aktif</span>
        <span>Assistant membaca stok</span>
        <span>Owner dapat insight</span>
      </div>
    </div>
  );
}

function ChatMessage({ message }) {
  const isUser = message.type === 'user';
  return (
    <div className={`sl-chat-row ${isUser ? 'is-user' : 'is-ai'}`}>
      {!isUser && (
        <div className="sl-chat-bot">
          <img src="/stockara-ai-icon.png" alt="" />
        </div>
      )}
      <div className="sl-chat-bubble">
        <p>{message.text}</p>
        {(message.productName || message.image) && (
          <div className="sl-product-card">
            {message.image && (
              <img
                src={message.image}
                alt={message.productName || 'Produk Stockara'}
                onError={(event) => {
                  event.currentTarget.src = '/stockara-product-fallback.svg';
                }}
              />
            )}
            <div>
              <strong>{message.productName}</strong>
              <span>{message.price}</span>
              {message.stock && <small>{message.stock}</small>}
            </div>
          </div>
        )}
        {message.proof && (
          <div className="sl-proof-photo">
            <img
              src={message.proof.image}
              alt={`Bukti transfer ${message.proof.amount} - ${message.proof.bank} Ref ${message.proof.ref}`}
            />
          </div>
        )}
        {message.invoice && (
          <div className="sl-invoice-photo">
            <img src={message.invoice.image} alt={`Invoice ${message.invoice.id} - ${message.invoice.business}`} />
          </div>
        )}
      </div>
    </div>
  );
}

function TypingIndicator({ side = 'ai' }) {
  const isUser = side === 'user';
  return (
    <div className={`sl-chat-row ${isUser ? 'is-user' : 'is-ai'}`}>
      {!isUser && (
        <div className="sl-chat-bot">
          <img src="/stockara-ai-icon.png" alt="" />
        </div>
      )}
      <div
        className={`sl-typing-indicator ${isUser ? 'is-user' : ''}`}
        aria-label={isUser ? 'Customer sedang mengetik' : 'Stockara AI sedang mengetik'}
      >
        <span />
        <span />
        <span />
      </div>
    </div>
  );
}

function AiShowcase() {
  return (
    <section id="ai" className="sl-section sl-ai-section">
      <div className="sl-container sl-ai-grid">
        <div className="sl-ai-copy">
          <SectionLabel>AI di setiap channel</SectionLabel>
          <h2>AI yang baca data bisnismu langsung, bukan chatbot yang buta stok.</h2>
          <p>
            AI CS jawab chat customer di WhatsApp pakai data stok dan harga yang sama dengan yang dipakai tim kasir.
            AI Assistant ringkas omzet, stok menipis, dan cashflow buat owner tanpa perlu buka laporan panjang.
          </p>
          <div className="sl-ai-metric-row">
            <div>
              <strong>3 detik</strong>
              <span>contoh respon CS</span>
            </div>
            <div>
              <strong>24/7</strong>
              <span>assistant standby</span>
            </div>
          </div>
          <div className="sl-ai-mini-list">
            {aiCards.map((item) => {
              const Icon = item.icon;
              return (
                <div className="sl-ai-mini-item" key={item.title}>
                  <Icon size={16} />
                  <div>
                    <strong>{item.title}</strong>
                    <span>{item.text}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <div className="sl-ai-visual">
          <AiVideoScene />
        </div>
      </div>
    </section>
  );
}

export default function LandingPageNew() {
  const [openIndex, setOpenIndex] = useState(0);
  const [activeIndustry, setActiveIndustry] = useState(industries[0].id);

  const currentYear = useMemo(() => new Date().getFullYear(), []);
  const selectedIndustry = useMemo(
    () => industries.find((item) => item.id === activeIndustry) || industries[0],
    [activeIndustry]
  );

  return (
    <div className="stockara-landing">
      <Header />

      <main>
        <section className="sl-hero" aria-labelledby="landing-title">
          <div className="sl-container sl-hero-content">
            <div className="sl-hero-copy">
              <h1 id="landing-title">Satu platform untuk semua yang bisnis kamu butuh, bukan cuma kasir.</h1>
              <p>
                Stok, marketplace, keuangan, manufaktur, sampai AI yang bantu jawab customer dan baca laporan,
                Stockara gabungin semuanya dalam satu sistem, bukan modul-modul terpisah yang harus disambung manual.
              </p>
              <div className="sl-hero-actions">
                <a className="sl-btn sl-btn-primary" href={STOCKARA_WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
                  Coba gratis 14 hari
                  <ArrowRight size={18} />
                </a>
                <a className="sl-btn sl-btn-ghost" href="#fitur">
                  Lihat semua modul
                </a>
              </div>
              <div className="sl-trust-line">
                <ShieldCheck size={17} />
                <span>Mulai dari trial, lalu aktifkan alur AI saat data bisnis sudah siap.</span>
              </div>
            </div>
            <div className="sl-hero-visual">
              <HeroModuleGrid />
            </div>
          </div>
        </section>

        <section className="sl-proof" aria-label="Ringkasan Stockara">
          <div className="sl-container sl-proof-grid">
            {stats.map((item) => (
              <div key={item.label} className="sl-proof-item">
                <strong>{item.value}</strong>
                <span>{item.label}</span>
              </div>
            ))}
          </div>
        </section>

        <section id="fitur" className="sl-section sl-differentiators">
          <div className="sl-container">
            <div className="sl-section-head sl-section-head-left">
              <SectionLabel>Kenapa Stockara</SectionLabel>
              <h2>Bukan software kasir yang ditempelin AI belakangan.</h2>
              <p>
                Banyak aplikasi kasir nambah fitur satu-satu supaya kelihatan lengkap. Stockara dibangun dari awal
                sebagai satu sistem, jadi setiap modul beneran nyambung, bukan cuma numpang tampil.
              </p>
            </div>
            <div className="sl-feature-grid">
              {differentiators.map((item) => (
                <FeatureCard key={item.title} feature={item} />
              ))}
            </div>
          </div>
        </section>

        <ModuleSection module={stockModule} />
        <ModuleSection module={financeModule} />
        <ModuleSection module={manufacturingModule} />
        <AiShowcase />

        <section id="industri" className="sl-section sl-industries">
          <div className="sl-container">
            <div className="sl-section-head">
              <SectionLabel>Bukti nyata</SectionLabel>
              <h2>Semua modul di atas, siap pakai untuk industrimu dari hari pertama.</h2>
              <p>
                Retail, FnB, fashion, dan manufaktur punya alur yang beda. Karena itu landing, dashboard, import,
                dan laporan disusun agar user tidak merasa masuk ke sistem yang asing.
              </p>
            </div>
            <div className="sl-industry-grid">
              {industries.map((item) => (
                <IndustryCard
                  key={item.title}
                  item={item}
                  active={item.id === activeIndustry}
                  onSelect={setActiveIndustry}
                />
              ))}
            </div>
            <IndustryDetail industry={selectedIndustry} />
          </div>
        </section>

        <section className="sl-section sl-workflow">
          <div className="sl-container sl-workflow-grid">
            <div>
              <SectionLabel>Cara mulai</SectionLabel>
              <h2>Mulainya pelan, hasilnya langsung terasa.</h2>
              <p>
                Tidak perlu memindahkan semua proses dalam satu hari. Mulai dari data paling penting, lalu tambah
                alur lain saat tim sudah nyaman.
              </p>
            </div>
            <div className="sl-workflow-list">
              {workflow.map((step, index) => {
                const Icon = step.icon;
                return (
                  <article key={step.title} className="sl-workflow-item">
                    <div className="sl-workflow-number">{String(index + 1).padStart(2, '0')}</div>
                    <div className="sl-workflow-icon">
                      <Icon size={20} />
                    </div>
                    <div>
                      <h3>{step.title}</h3>
                      <p>{step.text}</p>
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        <section id="harga" className="sl-section sl-pricing">
          <div className="sl-container">
            <div className="sl-section-head">
              <SectionLabel>Harga</SectionLabel>
              <h2>Pilih paket yang pas dulu. Naik kelas nanti gampang.</h2>
              <p>
                Harga dibuat jelas sejak awal. Mulai dari trial, lalu pilih paket sesuai jumlah SKU, user, cabang,
                dan kebutuhan integrasi.
              </p>
            </div>
            <div className="sl-price-grid">
              {plans.map((plan) => (
                <PricingCard key={plan.name} plan={plan} />
              ))}
            </div>
          </div>
        </section>

        <section id="faq" className="sl-section sl-faq">
          <div className="sl-container sl-faq-grid">
            <div>
              <SectionLabel>FAQ</SectionLabel>
              <h2>Pertanyaan yang biasanya muncul sebelum mulai.</h2>
              <p>
                Kalau kamu baru memindahkan bisnis dari catatan manual ke sistem, wajar kalau butuh kejelasan dulu.
              </p>
            </div>
            <div className="sl-faq-list">
              {faqs.map((item, index) => (
                <FaqItem
                  key={item.question}
                  item={item}
                  index={index}
                  openIndex={openIndex}
                  setOpenIndex={setOpenIndex}
                />
              ))}
            </div>
          </div>
        </section>

        <section className="sl-final-cta">
          <div className="sl-container sl-final-card">
            <SectionLabel>Ayo bicara</SectionLabel>
            <h2>Saatnya operasional bisnis kamu terasa lebih rapi.</h2>
            <p>
              Ceritakan alur toko, gudang, atau tim CS kamu. Stockara bantu susun setup yang paling pas sebelum trial dimulai.
            </p>
            <div className="sl-final-actions">
              <a className="sl-btn sl-btn-primary" href={STOCKARA_WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
                Konsultasi via WhatsApp
                <ArrowRight size={18} />
              </a>
              <a className="sl-btn sl-btn-light" href="#harga">
                Lihat paket Stockara
              </a>
            </div>
          </div>
        </section>
      </main>

      <footer className="sl-footer">
        <div className="sl-container sl-footer-inner">
          <div className="sl-footer-brand">
            <img src="/logo-stockara.png" alt="Stockara" />
            <p>Stok, kasir, keuangan, manufaktur, marketplace, dan AI partner bisnis dalam satu platform.</p>
          </div>
          <div className="sl-footer-links">
            <div>
              <p>Produk</p>
              <a href="#fitur">Fitur</a>
              <a href="#industri">Template bisnis</a>
              <a href="#harga">Harga</a>
            </div>
            <div>
              <p>Solusi</p>
              <a href="#stok-marketplace">Stok & Marketplace</a>
              <a href="#keuangan">Keuangan</a>
              <a href="#manufaktur">Manufaktur</a>
              <a href="#ai">AI CS & Assistant</a>
            </div>
            <div>
              <p>Kontak</p>
              <a href={STOCKARA_WHATSAPP_URL} target="_blank" rel="noopener noreferrer">WhatsApp</a>
              <a href="mailto:Stockara.cs@gmail.com">Stockara.cs@gmail.com</a>
              <a href="/login">Masuk</a>
              <a href={STOCKARA_WHATSAPP_URL} target="_blank" rel="noopener noreferrer">Coba gratis 14 hari</a>
            </div>
          </div>
        </div>
        <div className="sl-container sl-footer-address">
          <p>Jl. Bayur, RT 038 RW 000, Kel. Sempaja Utara, Kec. Samarinda Utara, Kota Samarinda, Kalimantan Timur 75119</p>
        </div>
        <div className="sl-container sl-footer-bottom">
          <p>Copyright {currentYear} Stockara. Semua hak dilindungi.</p>
          <div>
            <a href="/terms">Syarat & Ketentuan</a>
            <a href="/privacy">Kebijakan Privasi</a>
          </div>
        </div>
      </footer>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

        .stockara-landing {
          --sl-green: #097049;
          --sl-green-dark: #075d3e;
          --sl-green-2: #0bbf7a;
          --sl-ink: #081b13;
          --sl-muted: #475f55;
          --sl-line: #b9d9ca;
          --sl-bg: #f6f1ea;
          --sl-bg-2: #fffaf4;
          --sl-card: #ffffff;
          --sl-soft: #e5f4ec;
          --sl-dark: #06160f;
          --sl-display-font: "Instrument Serif", Georgia, "Times New Roman", serif;
          --sl-body-font: "Plus Jakarta Sans", Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
          background: var(--sl-bg);
          color: var(--sl-ink);
          font-family: var(--sl-body-font);
          letter-spacing: 0;
          overflow-x: hidden;
        }

        @keyframes sl-gradient-drift {
          0% { transform: translate3d(-2%, -1%, 0) scale(1); }
          50% { transform: translate3d(2%, 2%, 0) scale(1.04); }
          100% { transform: translate3d(-1%, 3%, 0) scale(1.02); }
        }

        .stockara-landing *,
        .stockara-landing *::before,
        .stockara-landing *::after {
          box-sizing: border-box;
        }

        .stockara-landing a {
          color: inherit;
          text-decoration: none;
        }

        .sl-container {
          width: min(1160px, calc(100% - 40px));
          margin: 0 auto;
        }

        .sl-header {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          z-index: 50;
          background: rgba(246, 241, 234, 0.94);
          border-bottom: 1px solid rgba(185, 217, 202, 0.72);
          backdrop-filter: blur(14px);
        }

        .sl-header::before {
          content: "";
          position: absolute;
          inset: 0;
          z-index: -1;
          background:
            radial-gradient(circle at 22% 18%, rgba(22, 199, 132, 0.13), transparent 32%),
            radial-gradient(circle at 82% 78%, rgba(9, 112, 73, 0.10), transparent 30%);
          animation: sl-gradient-drift 24s ease-in-out infinite alternate;
        }

        .sl-header.is-scrolled {
          background: rgba(246, 241, 234, 0.98);
          box-shadow: 0 12px 34px rgba(8, 27, 19, 0.09);
        }

        .sl-nav-shell {
          width: min(1180px, calc(100% - 40px));
          height: 72px;
          margin: 0 auto;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 24px;
          position: relative;
        }

        .sl-logo-link {
          display: inline-flex;
          align-items: center;
          flex: 0 0 auto;
        }

        .sl-logo-img {
          display: block;
          width: 142px;
          height: 40px;
          object-fit: cover;
          object-position: center;
        }

        .sl-nav-links,
        .sl-nav-actions {
          display: flex;
          align-items: center;
          gap: 24px;
        }

        .sl-nav-links a,
        .sl-login-link {
          color: #334155;
          font-size: 14px;
          font-weight: 700;
          line-height: 1;
        }

        .sl-nav-links a:hover,
        .sl-login-link:hover {
          color: var(--sl-green);
        }

        .sl-menu-btn {
          display: none;
          width: 42px;
          height: 42px;
          border: 1px solid var(--sl-line);
          border-radius: 8px;
          align-items: center;
          justify-content: center;
          color: var(--sl-ink);
          background: var(--sl-bg-2);
          cursor: pointer;
        }

        .sl-mobile-panel {
          display: none;
        }

        .sl-btn {
          min-height: 44px;
          border-radius: 8px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 0 18px;
          border: 1px solid transparent;
          font-weight: 800;
          font-size: 14px;
          line-height: 1;
          white-space: nowrap;
          transition: transform 0.18s ease, box-shadow 0.18s ease, background 0.18s ease;
        }

        .sl-btn:hover {
          transform: translateY(-1px);
        }

        .stockara-landing .sl-btn-primary {
          background: var(--sl-green);
          color: #fff;
          box-shadow: 0 14px 28px rgba(6, 114, 71, 0.22);
        }

        .stockara-landing .sl-btn-primary:hover {
          background: #055f3d;
        }

        .sl-btn-small {
          min-height: 40px;
          padding: 0 16px;
        }

        .stockara-landing .sl-btn-ghost {
          background: rgba(255, 255, 255, 0.9);
          color: var(--sl-ink);
          border-color: rgba(255, 255, 255, 0.62);
        }

        .stockara-landing .sl-btn-soft {
          background: var(--sl-soft);
          color: var(--sl-green);
          border-color: var(--sl-line);
        }

        .sl-hero {
          position: relative;
          min-height: 84svh;
          padding: 126px 0 66px;
          display: flex;
          align-items: center;
          overflow: hidden;
          background: var(--sl-bg);
          border-bottom: 1px solid var(--sl-line);
        }

        .sl-hero::before {
          content: "";
          position: absolute;
          inset: 0;
          background-image:
            linear-gradient(rgba(6, 114, 71, 0.115) 1px, transparent 1px),
            linear-gradient(90deg, rgba(6, 114, 71, 0.115) 1px, transparent 1px);
          background-size: 54px 54px;
          opacity: 0.52;
          pointer-events: none;
        }

        .sl-hero::after {
          content: "";
          position: absolute;
          inset: -24%;
          z-index: 0;
          pointer-events: none;
          background:
            radial-gradient(circle at 16% 20%, rgba(22, 199, 132, 0.20), transparent 31%),
            radial-gradient(circle at 82% 76%, rgba(9, 112, 73, 0.13), transparent 28%),
            radial-gradient(circle at 52% 46%, rgba(255, 250, 244, 0.76), transparent 38%);
          animation: sl-gradient-drift 26s ease-in-out infinite alternate;
        }

        .sl-hero-content {
          position: relative;
          z-index: 1;
          display: grid;
          grid-template-columns: minmax(0, 0.95fr) minmax(360px, 0.85fr);
          gap: 54px;
          align-items: center;
        }

        .sl-hero-copy {
          width: min(680px, 100%);
          color: var(--sl-ink);
        }

        .sl-eyebrow,
        .sl-section-label {
          margin: 0 0 14px;
          color: var(--sl-green-2);
          font-size: 12px;
          line-height: 1.2;
          font-weight: 900;
          text-transform: uppercase;
          letter-spacing: 0;
        }

        .sl-hero h1 {
          margin: 0;
          max-width: 720px;
          font-family: var(--sl-display-font);
          font-size: clamp(40px, 6vw, 72px);
          line-height: 0.98;
          font-weight: 400;
          letter-spacing: 0;
        }

        .sl-hero-copy > p:not(.sl-eyebrow) {
          margin: 22px 0 0;
          max-width: 590px;
          color: #475569;
          font-size: clamp(16px, 1.8vw, 20px);
          line-height: 1.7;
          letter-spacing: 0;
        }

        .sl-hero-actions {
          margin-top: 34px;
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
        }

        .sl-trust-line {
          margin-top: 20px;
          display: flex;
          align-items: center;
          gap: 8px;
          color: #526070;
          font-size: 14px;
          line-height: 1.5;
        }

        .sl-trust-line svg {
          color: var(--sl-green);
          flex: 0 0 auto;
        }

        .sl-hero-visual {
          position: relative;
          min-width: 0;
        }

        .sl-chat-demo-frame {
          position: relative;
          background: #fff;
          border: 1px solid var(--sl-line);
          border-radius: 8px;
          box-shadow: 0 28px 70px rgba(15, 23, 42, 0.12);
          overflow: hidden;
        }

        .sl-chat-demo-toolbar {
          height: 44px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          padding: 0 16px;
          color: #0f172a;
          font-size: 13px;
          font-weight: 900;
          border-bottom: 1px solid var(--sl-line);
          background: #fff;
        }

        .sl-chat-demo-toolbar > div {
          display: flex;
          align-items: center;
          gap: 10px;
          min-width: 0;
        }

        .sl-video-dot {
          width: 10px;
          height: 10px;
          border-radius: 999px;
          background: var(--sl-green);
          box-shadow: 0 0 0 6px rgba(6, 114, 71, 0.12);
          flex: 0 0 auto;
        }

        .sl-toolbar-logo {
          width: 24px;
          height: 24px;
          border-radius: 999px;
          object-fit: cover;
          background: #f4efe6;
          border: 1px solid rgba(6, 114, 71, 0.16);
        }

        .sl-chat-reset {
          appearance: none;
          border: 1px solid rgba(6, 114, 71, 0.18);
          background: #f2fbf6;
          color: var(--sl-green);
          border-radius: 999px;
          min-height: 30px;
          padding: 0 10px;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font: inherit;
          font-size: 12px;
          font-weight: 900;
          cursor: pointer;
        }

        .sl-chat-demo-body {
          padding: 18px;
          background:
            linear-gradient(rgba(6, 114, 71, 0.025) 1px, transparent 1px),
            linear-gradient(90deg, rgba(6, 114, 71, 0.025) 1px, transparent 1px),
            #fbf8f2;
          background-size: 28px 28px;
        }

        .sl-industry-tabs {
          display: flex;
          justify-content: center;
          gap: 6px;
          width: fit-content;
          max-width: 100%;
          margin: 0 auto 16px;
          padding: 6px;
          background: #eef1ed;
          border-radius: 999px;
          overflow-x: auto;
        }

        .sl-industry-tabs button {
          appearance: none;
          border: 0;
          border-radius: 999px;
          background: transparent;
          color: #64748b;
          cursor: pointer;
          font: inherit;
          font-size: 12px;
          font-weight: 900;
          line-height: 1;
          min-height: 32px;
          padding: 0 13px;
          white-space: nowrap;
          transition: background 0.18s ease, color 0.18s ease, box-shadow 0.18s ease;
        }

        .sl-industry-tabs button:hover,
        .sl-industry-tabs button.is-active {
          background: #fff;
          color: var(--sl-green);
          box-shadow: 0 8px 18px rgba(15, 23, 42, 0.08);
        }

        .sl-chat-phone {
          border: 1px solid rgba(6, 114, 71, 0.18);
          border-radius: 22px;
          background: #fff;
          overflow: hidden;
          height: 420px;
          max-height: 420px;
          display: flex;
          flex-direction: column;
          box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.72);
        }

        .sl-chat-header {
          min-height: 58px;
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 16px;
          background: #f7fbf8;
          border-bottom: 1px solid var(--sl-line);
        }

        .sl-chat-avatar {
          width: 36px;
          height: 36px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 999px;
          background: #dff8eb;
          color: var(--sl-green);
          flex: 0 0 auto;
        }

        .sl-chat-avatar img,
        .sl-chat-bot img {
          width: 100%;
          height: 100%;
          border-radius: inherit;
          object-fit: cover;
          display: block;
        }

        .sl-chat-header strong,
        .sl-chat-header span {
          display: block;
        }

        .sl-chat-header strong {
          color: var(--sl-ink);
          font-size: 14px;
          font-weight: 950;
        }

        .sl-chat-header span {
          color: var(--sl-muted);
          font-size: 12px;
          font-weight: 750;
          margin-top: 2px;
        }

        .sl-chat-messages {
          flex: 1;
          min-height: 0;
          height: 292px;
          max-height: 292px;
          overflow-y: auto;
          padding: 16px;
          display: flex;
          flex-direction: column;
          gap: 12px;
          scrollbar-width: thin;
          scrollbar-color: rgba(6, 114, 71, 0.45) transparent;
        }

        .sl-chat-messages::-webkit-scrollbar {
          width: 7px;
        }

        .sl-chat-messages::-webkit-scrollbar-track {
          background: transparent;
        }

        .sl-chat-messages::-webkit-scrollbar-thumb {
          background: rgba(6, 114, 71, 0.38);
          border-radius: 999px;
        }

        .sl-chat-row {
          display: flex;
          align-items: flex-end;
          gap: 8px;
          animation: sl-fade-in-up 0.28s ease both;
        }

        .sl-chat-row.is-user {
          justify-content: flex-end;
        }

        .sl-chat-bot {
          width: 26px;
          height: 26px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 999px;
          background: #f4efe6;
          color: var(--sl-green);
          border: 1px solid rgba(6, 114, 71, 0.16);
          overflow: hidden;
          flex: 0 0 auto;
        }

        .sl-chat-bubble,
        .sl-typing-indicator {
          max-width: 78%;
          border-radius: 18px;
          padding: 11px 13px;
          border: 1px solid transparent;
        }

        .sl-chat-row.is-user .sl-chat-bubble {
          background: #f2f4f3;
          color: #172033;
          border-bottom-right-radius: 6px;
        }

        .sl-chat-row.is-ai .sl-chat-bubble,
        .sl-typing-indicator {
          background: #ecfbf3;
          color: #10251c;
          border-color: rgba(6, 114, 71, 0.12);
          border-bottom-left-radius: 6px;
        }

        .sl-chat-bubble p {
          margin: 0;
          font-size: 13px;
          line-height: 1.48;
          color: inherit;
        }

        .sl-product-card {
          display: grid;
          grid-template-columns: 56px minmax(0, 1fr);
          gap: 10px;
          margin-top: 10px;
          padding: 8px;
          border-radius: 14px;
          background: #fff;
          border: 1px solid rgba(6, 114, 71, 0.12);
        }

        .sl-product-card img {
          width: 56px;
          height: 56px;
          border-radius: 10px;
          object-fit: cover;
          background: #eef2f0;
        }

        .sl-product-card strong,
        .sl-product-card span,
        .sl-product-card small {
          display: block;
        }

        .sl-product-card strong {
          color: var(--sl-ink);
          font-size: 12px;
          font-weight: 950;
          line-height: 1.25;
        }

        .sl-product-card span {
          margin-top: 4px;
          color: var(--sl-green);
          font-size: 12px;
          font-weight: 950;
        }

        .sl-product-card small {
          margin-top: 2px;
          color: #64748b;
          font-size: 11px;
          font-weight: 800;
        }

        .sl-proof-photo,
        .sl-invoice-photo {
          margin-top: 10px;
          max-width: 100%;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 4px 14px rgba(15, 23, 42, 0.16);
        }

        .sl-proof-photo {
          width: 168px;
        }

        .sl-invoice-photo {
          width: 260px;
        }

        .sl-proof-photo img,
        .sl-invoice-photo img {
          display: block;
          width: 100%;
          height: auto;
        }

        .sl-typing-indicator {
          display: flex;
          align-items: center;
          gap: 5px;
          min-height: 40px;
          width: fit-content;
        }

        .sl-typing-indicator.is-user {
          background: #f2f4f3;
          border-color: transparent;
          border-bottom-right-radius: 6px;
        }

        .sl-typing-indicator.is-user span {
          background: #64748b;
        }

        .sl-typing-indicator span {
          width: 7px;
          height: 7px;
          border-radius: 999px;
          background: var(--sl-green);
          animation: sl-typing-dot 1s ease-in-out infinite;
        }

        .sl-typing-indicator span:nth-child(2) {
          animation-delay: 0.14s;
        }

        .sl-typing-indicator span:nth-child(3) {
          animation-delay: 0.28s;
        }

        .sl-chat-input {
          min-height: 58px;
          padding: 10px;
          display: flex;
          align-items: center;
          gap: 8px;
          border-top: 1px solid var(--sl-line);
          background: #fff;
        }

        .sl-chat-input input {
          flex: 1;
          min-width: 0;
          min-height: 38px;
          padding: 0 12px;
          border-radius: 13px;
          border: 0;
          outline: none;
          background: #f7f8f7;
          color: #172033;
          font-size: 13px;
          font-weight: 700;
        }

        .sl-chat-input input::placeholder {
          color: #94a3b8;
        }

        .sl-chat-input input:focus {
          box-shadow: 0 0 0 2px rgba(6, 114, 71, 0.16);
        }

        .sl-chat-input button {
          appearance: none;
          border: 0;
          min-height: 38px;
          border-radius: 13px;
          padding: 0 14px;
          background: var(--sl-green);
          color: #fff;
          font: inherit;
          font-size: 12px;
          font-weight: 950;
        }

        @keyframes sl-fade-in-up {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes sl-typing-dot {
          0%,
          80%,
          100% {
            opacity: 0.35;
            transform: translateY(0);
          }
          40% {
            opacity: 1;
            transform: translateY(-3px);
          }
        }

        .sl-video-footer {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 1px;
          background: var(--sl-line);
          border-top: 1px solid var(--sl-line);
        }

        .sl-video-footer span {
          min-height: 52px;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 10px;
          background: #fff;
          color: #334155;
          font-size: 12px;
          font-weight: 850;
          line-height: 1.35;
          text-align: center;
        }

        .sl-proof {
          background: var(--sl-bg);
          border-bottom: 1px solid var(--sl-line);
        }

        .sl-proof-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 1px;
          background: var(--sl-line);
        }

        .sl-proof-item {
          background: var(--sl-bg-2);
          min-height: 112px;
          display: flex;
          flex-direction: column;
          justify-content: center;
          padding: 22px;
        }

        .sl-proof-item strong {
          display: block;
          font-family: var(--sl-display-font);
          font-size: clamp(25px, 3vw, 36px);
          line-height: 0.96;
          font-weight: 400;
          color: var(--sl-ink);
          letter-spacing: 0;
        }

        .sl-proof-item span {
          margin-top: 9px;
          color: var(--sl-muted);
          font-size: 14px;
          font-weight: 700;
        }

        .sl-section {
          padding: 96px 0;
        }

        .sl-section,
        .sl-proof,
        .sl-final-cta {
          position: relative;
          overflow: hidden;
        }

        .sl-section > .sl-container,
        .sl-proof > .sl-container,
        .sl-final-cta > .sl-container {
          position: relative;
          z-index: 1;
        }

        .sl-industries::before,
        .sl-features::before,
        .sl-pricing::before,
        .sl-workflow::before,
        .sl-faq::before,
        .sl-proof::before {
          content: "";
          position: absolute;
          inset: -22%;
          z-index: 0;
          pointer-events: none;
          background:
            linear-gradient(rgba(6, 114, 71, 0.085) 1px, transparent 1px),
            linear-gradient(90deg, rgba(6, 114, 71, 0.085) 1px, transparent 1px),
            radial-gradient(circle at 18% 18%, rgba(22, 199, 132, 0.13), transparent 30%),
            radial-gradient(circle at 80% 72%, rgba(9, 112, 73, 0.09), transparent 30%),
            radial-gradient(circle at 54% 50%, rgba(255, 250, 244, 0.62), transparent 38%);
          background-size: 48px 48px, 48px 48px, auto, auto, auto;
          animation: sl-gradient-drift 28s ease-in-out infinite alternate;
        }

        .sl-section-head {
          width: min(760px, 100%);
          margin: 0 auto 42px;
          text-align: center;
        }

        .sl-section-head-left {
          margin-left: 0;
          text-align: left;
        }

        .sl-section h2,
        .sl-final-cta h2 {
          margin: 0;
          color: var(--sl-ink);
          font-family: var(--sl-display-font);
          font-size: clamp(30px, 4.4vw, 48px);
          line-height: 1.02;
          font-weight: 400;
          letter-spacing: 0;
        }

        .sl-section-head p,
        .sl-workflow-grid > div:first-child p,
        .sl-ai-copy p,
        .sl-faq-grid > div:first-child p,
        .sl-final-cta p {
          margin: 16px 0 0;
          color: var(--sl-muted);
          font-size: 17px;
          line-height: 1.72;
          letter-spacing: 0;
        }

        .sl-ai-section {
          color: var(--sl-ink);
          background:
            radial-gradient(circle at 18% 28%, rgba(27, 220, 150, 0.2), transparent 32%),
            radial-gradient(circle at 84% 18%, rgba(194, 236, 156, 0.36), transparent 28%),
            radial-gradient(circle at 72% 82%, rgba(10, 112, 73, 0.13), transparent 30%),
            linear-gradient(135deg, #eefdf5 0%, #d9f8ee 45%, #f7f1e8 100%);
        }

        .sl-ai-section::before,
        .sl-final-cta::before {
          content: "";
          position: absolute;
          inset: 0;
          z-index: 0;
          pointer-events: none;
          background:
            linear-gradient(rgba(9, 112, 73, 0.06) 1px, transparent 1px),
            linear-gradient(90deg, rgba(9, 112, 73, 0.06) 1px, transparent 1px),
            radial-gradient(circle at 92% 8%, rgba(9, 112, 73, 0.12) 1px, transparent 1px);
          background-size: 38px 38px, 38px 38px, 8px 8px;
          mask-image: linear-gradient(90deg, transparent, #000 12%, #000 88%, transparent);
        }

        .sl-ai-grid {
          display: grid;
          grid-template-columns: minmax(0, 0.85fr) minmax(0, 1.15fr);
          gap: 42px;
          align-items: center;
        }

        .sl-ai-section h2 {
          color: var(--sl-ink);
        }

        .sl-ai-copy p {
          color: rgba(8, 27, 19, 0.66);
        }

        .sl-ai-metric-row {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 12px;
          margin-top: 26px;
        }

        .sl-ai-metric-row div,
        .sl-ai-card {
          border: 1px solid var(--sl-line);
          border-radius: 8px;
          background: rgba(255, 255, 255, 0.6);
        }

        .sl-ai-metric-row div {
          padding: 18px;
        }

        .sl-ai-metric-row strong {
          display: block;
          font-family: var(--sl-display-font);
          color: var(--sl-green);
          font-size: 28px;
          line-height: 0.96;
          font-weight: 400;
        }

        .sl-ai-metric-row span {
          display: block;
          margin-top: 8px;
          color: rgba(8, 27, 19, 0.6);
          font-size: 13px;
          font-weight: 800;
        }

        .sl-ai-card-grid {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 14px;
        }

        .sl-ai-card {
          min-height: 224px;
          padding: 22px;
        }

        .sl-ai-card-icon {
          width: 40px;
          height: 40px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #fff;
          color: var(--sl-green);
          margin-bottom: 16px;
        }

        .sl-ai-card h3 {
          margin: 0;
          color: var(--sl-ink);
          font-family: var(--sl-display-font);
          font-size: 24px;
          line-height: 1.12;
          font-weight: 400;
        }

        .sl-ai-card p {
          margin: 10px 0 0;
          color: rgba(8, 27, 19, 0.66);
          font-size: 14px;
          line-height: 1.68;
        }

        .sl-industries {
          background: var(--sl-bg);
        }

        .sl-industry-grid {
          display: grid;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          gap: 16px;
        }

        .sl-industry-card {
          appearance: none;
          width: 100%;
          text-align: left;
          cursor: pointer;
          background: var(--sl-bg-2);
          border: 1px solid var(--sl-line);
          border-radius: 8px;
          overflow: hidden;
          min-width: 0;
          padding: 0;
          font: inherit;
          color: inherit;
          transition: border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease;
        }

        .sl-industry-card:hover,
        .sl-industry-card.is-active {
          border-color: var(--sl-green);
          box-shadow: 0 18px 40px rgba(9, 112, 73, 0.15);
          transform: translateY(-2px);
        }

        .sl-industry-media {
          height: 150px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: var(--sl-soft);
          border-bottom: 1px solid var(--sl-line);
        }

        .sl-industry-card.tone-blue .sl-industry-media {
          background: var(--sl-soft);
        }

        .sl-industry-card.tone-amber .sl-industry-media {
          background: var(--sl-soft);
        }

        .sl-industry-card.tone-violet .sl-industry-media {
          background: var(--sl-soft);
        }

        .sl-industry-media img {
          width: 108px;
          max-width: 72%;
          height: 108px;
          object-fit: contain;
        }

        .sl-industry-body {
          padding: 20px;
        }

        .sl-industry-icon,
        .sl-feature-icon,
        .sl-workflow-icon {
          width: 38px;
          height: 38px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #e5f7ef;
          color: var(--sl-green);
          margin-bottom: 14px;
        }

        .sl-industry-card.tone-blue .sl-industry-icon {
          background: #e5f7ef;
          color: var(--sl-green);
        }

        .sl-industry-card.tone-amber .sl-industry-icon {
          background: #e5f7ef;
          color: var(--sl-green);
        }

        .sl-industry-card.tone-violet .sl-industry-icon {
          background: #e5f7ef;
          color: var(--sl-green);
        }

        .sl-industry-card h3,
        .sl-feature-card h3,
        .sl-workflow-item h3,
        .sl-price-card h3 {
          margin: 0;
          color: var(--sl-ink);
          font-family: var(--sl-display-font);
          font-size: 24px;
          line-height: 1.12;
          font-weight: 400;
          letter-spacing: 0;
        }

        .sl-industry-card p,
        .sl-feature-card p,
        .sl-workflow-item p {
          margin: 10px 0 0;
          color: var(--sl-muted);
          font-size: 14px;
          line-height: 1.68;
          letter-spacing: 0;
        }

        .sl-industry-action {
          display: flex;
          align-items: center;
          justify-content: center;
          min-height: 42px;
          margin: 0 20px 20px;
          border-radius: 8px;
          background: var(--sl-soft);
          color: var(--sl-green);
          font-size: 13px;
          font-weight: 900;
        }

        .sl-industry-card.is-active .sl-industry-action {
          background: var(--sl-green);
          color: #fff;
        }

        .sl-industry-detail {
          margin-top: 18px;
          display: grid;
          grid-template-columns: minmax(220px, 0.62fr) minmax(0, 1.38fr);
          grid-template-areas:
            "head copy"
            "features features"
            "cta cta";
          gap: 24px;
          align-items: start;
          position: relative;
          overflow: hidden;
          color: var(--sl-ink);
          border: 1px solid var(--sl-line);
          border-radius: 14px;
          padding: 26px;
          background:
            radial-gradient(circle at 18% 28%, rgba(27, 220, 150, 0.2), transparent 32%),
            radial-gradient(circle at 84% 18%, rgba(194, 236, 156, 0.36), transparent 28%),
            radial-gradient(circle at 72% 82%, rgba(10, 112, 73, 0.13), transparent 30%),
            linear-gradient(135deg, #eefdf5 0%, #d9f8ee 45%, #f7f1e8 100%);
          box-shadow: 0 32px 80px rgba(9, 112, 73, 0.1);
        }

        .sl-industry-detail::before {
          content: "";
          position: absolute;
          inset: 0;
          pointer-events: none;
          background:
            linear-gradient(rgba(9, 112, 73, 0.06) 1px, transparent 1px),
            linear-gradient(90deg, rgba(9, 112, 73, 0.06) 1px, transparent 1px),
            radial-gradient(circle at 92% 8%, rgba(9, 112, 73, 0.12) 1px, transparent 1px);
          background-size: 38px 38px, 38px 38px, 8px 8px;
          mask-image: linear-gradient(90deg, transparent, #000 12%, #000 88%, transparent);
        }

        .sl-industry-detail > * {
          position: relative;
          z-index: 1;
        }

        .sl-industry-detail-head {
          grid-area: head;
          display: flex;
          align-items: center;
          gap: 14px;
        }

        .sl-industry-detail-copy {
          grid-area: copy;
          min-width: 0;
        }

        .sl-industry-detail-icon {
          width: 54px;
          height: 54px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #fff;
          color: var(--sl-green);
          box-shadow: 0 8px 18px rgba(9, 112, 73, 0.14);
          flex: 0 0 auto;
        }

        .sl-industry-detail-head p {
          margin: 0 0 6px;
          color: rgba(8, 27, 19, 0.56);
          font-size: 12px;
          font-weight: 900;
          text-transform: uppercase;
        }

        .sl-industry-detail h3,
        .sl-industry-detail h4 {
          margin: 0;
          color: var(--sl-ink);
          letter-spacing: 0;
        }

        .sl-industry-detail h3 {
          font-family: var(--sl-display-font);
          font-size: 24px;
          line-height: 1.1;
          font-weight: 400;
        }

        .sl-industry-detail h4 {
          font-family: var(--sl-display-font);
          font-size: 20px;
          line-height: 1.28;
          font-weight: 400;
        }

        .sl-industry-detail-copy p {
          margin: 10px 0 0;
          color: rgba(8, 27, 19, 0.66);
          font-size: 14px;
          line-height: 1.7;
        }

        .sl-template-feature-grid {
          grid-area: features;
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 10px;
        }

        .sl-template-feature {
          display: grid;
          grid-template-columns: 20px minmax(0, 1fr);
          gap: 9px;
          align-items: start;
          color: rgba(8, 27, 19, 0.78);
          font-size: 13px;
          line-height: 1.45;
          font-weight: 750;
        }

        .sl-template-feature svg {
          color: var(--sl-green);
          margin-top: 1px;
        }

        .sl-template-cta {
          width: max-content;
          grid-area: cta;
          justify-self: start;
        }

        .sl-features,
        .sl-differentiators {
          background: var(--sl-bg-2);
        }

        .sl-module-detail {
          margin-top: 0;
        }

        .sl-module-alt {
          background: var(--sl-bg-2);
        }

        .sl-hero-modules {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 12px;
          background: #fff;
          border: 1px solid var(--sl-line);
          border-radius: 8px;
          padding: 20px;
          box-shadow: 0 28px 70px rgba(15, 23, 42, 0.12);
        }

        .sl-hero-module-tile {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 14px;
          border-radius: 8px;
          background: var(--sl-soft);
        }

        .sl-hero-module-icon {
          width: 34px;
          height: 34px;
          flex: 0 0 auto;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #fff;
          color: var(--sl-green);
        }

        .sl-hero-module-tile span {
          font-size: 13px;
          font-weight: 800;
          color: var(--sl-ink);
          line-height: 1.25;
        }

        .sl-ai-visual {
          position: relative;
          min-width: 0;
        }

        .sl-ai-mini-list {
          display: grid;
          gap: 12px;
          margin-top: 22px;
        }

        .sl-ai-mini-item {
          display: grid;
          grid-template-columns: 16px minmax(0, 1fr);
          gap: 10px;
          align-items: start;
          color: rgba(8, 27, 19, 0.6);
        }

        .sl-ai-mini-item svg {
          margin-top: 3px;
          color: var(--sl-green);
        }

        .sl-ai-mini-item strong {
          display: block;
          color: var(--sl-ink);
          font-size: 13px;
          font-weight: 800;
        }

        .sl-ai-mini-item span {
          display: block;
          margin-top: 2px;
          font-size: 13px;
          line-height: 1.55;
        }

        .sl-feature-grid {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 16px;
        }

        .sl-feature-card {
          background: var(--sl-card);
          border: 1px solid var(--sl-line);
          border-radius: 8px;
          padding: 24px;
          min-height: 230px;
          min-width: 0;
          box-shadow: 0 16px 36px rgba(15, 23, 42, 0.05);
        }

        .sl-workflow {
          background: var(--sl-soft);
          border-top: 1px solid var(--sl-line);
          border-bottom: 1px solid var(--sl-line);
        }

        .sl-workflow-grid {
          display: grid;
          grid-template-columns: minmax(0, 0.85fr) minmax(0, 1.15fr);
          gap: 52px;
          align-items: start;
        }

        .sl-workflow-list {
          display: grid;
          gap: 14px;
        }

        .sl-workflow-item {
          display: grid;
          grid-template-columns: 58px 44px minmax(0, 1fr);
          gap: 16px;
          align-items: start;
          background: var(--sl-bg-2);
          border: 1px solid var(--sl-line);
          border-radius: 8px;
          padding: 20px;
        }

        .sl-workflow-number {
          color: #9aa8a2;
          font-family: var(--sl-display-font);
          font-size: 20px;
          line-height: 38px;
          font-weight: 400;
        }

        .sl-workflow-icon {
          margin: 0;
        }

        .sl-pricing {
          background: var(--sl-bg);
        }

        .sl-price-grid {
          display: grid;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          gap: 16px;
          align-items: stretch;
        }

        .sl-price-card {
          position: relative;
          min-width: 0;
          display: flex;
          flex-direction: column;
          background: var(--sl-bg-2);
          border: 1px solid var(--sl-line);
          border-radius: 8px;
          padding: 24px 20px;
          box-shadow: 0 16px 34px rgba(15, 23, 42, 0.06);
          overflow: visible;
        }

        .sl-price-card.is-highlighted {
          border-color: rgba(6, 114, 71, 0.52);
          box-shadow: 0 22px 44px rgba(6, 114, 71, 0.15);
        }

        .sl-price-badge {
          position: absolute;
          top: -14px;
          left: 16px;
          right: 16px;
          height: 28px;
          border-radius: 8px;
          background: var(--sl-green);
          color: #fff;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 12px;
          font-weight: 900;
          white-space: nowrap;
        }

        .sl-price-head {
          min-height: 94px;
          padding-top: 4px;
        }

        .sl-price-card.is-highlighted .sl-price-head {
          padding-top: 12px;
        }

        .sl-price-head p {
          margin: 10px 0 0;
          color: var(--sl-muted);
          font-size: 14px;
          line-height: 1.55;
        }

        .sl-price-row {
          margin: 12px 0 20px;
          min-height: 72px;
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          justify-content: center;
          gap: 8px;
          overflow: visible;
        }

        .sl-price-value {
          display: block;
          max-width: 100%;
          color: var(--sl-ink);
          font-family: var(--sl-display-font);
          font-size: clamp(28px, 2.7vw, 36px);
          line-height: 0.96;
          font-weight: 400;
          letter-spacing: 0;
          white-space: nowrap;
          overflow: visible;
        }

        .sl-price-period {
          display: block;
          color: var(--sl-muted);
          font-size: 14px;
          font-weight: 800;
          white-space: nowrap;
        }

        .sl-price-cta {
          width: 100%;
          margin-bottom: 20px;
        }

        .sl-price-card ul {
          list-style: none;
          margin: 0;
          padding: 0;
          display: grid;
          gap: 12px;
        }

        .sl-price-card li {
          display: grid;
          grid-template-columns: 18px minmax(0, 1fr);
          gap: 8px;
          align-items: start;
          color: #334155;
          font-size: 14px;
          line-height: 1.5;
          font-weight: 650;
        }

        .sl-price-card li svg {
          color: var(--sl-green);
          margin-top: 2px;
        }

        .sl-faq {
          background: var(--sl-soft);
        }

        .sl-faq-grid {
          display: grid;
          grid-template-columns: minmax(0, 0.85fr) minmax(0, 1.15fr);
          gap: 46px;
          align-items: start;
        }

        .sl-faq-list {
          border: 1px solid var(--sl-line);
          border-radius: 8px;
          overflow: hidden;
          background: var(--sl-bg-2);
        }

        .sl-faq-item + .sl-faq-item {
          border-top: 1px solid var(--sl-line);
        }

        .sl-faq-item button {
          width: 100%;
          min-height: 64px;
          border: 0;
          background: var(--sl-bg-2);
          color: var(--sl-ink);
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          padding: 18px 20px;
          text-align: left;
          font-family: var(--sl-display-font);
          font-size: 21px;
          font-weight: 400;
          cursor: pointer;
          letter-spacing: 0;
        }

        .sl-faq-toggle {
          width: 28px;
          height: 28px;
          border-radius: 8px;
          background: #e9f7f0;
          color: var(--sl-green);
          display: flex;
          align-items: center;
          justify-content: center;
          flex: 0 0 auto;
          font-size: 18px;
          line-height: 1;
        }

        .sl-faq-item p {
          margin: 0;
          padding: 0 20px 20px;
          color: var(--sl-muted);
          font-size: 14px;
          line-height: 1.7;
        }

        .sl-final-cta {
          padding: 94px 0 64px;
          background: #fff;
          color: var(--sl-ink);
        }

        .sl-final-card {
          min-height: 330px;
          border: 1px solid var(--sl-line);
          border-radius: 14px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          padding: 58px 24px;
          overflow: hidden;
          background:
            radial-gradient(circle at 18% 28%, rgba(27, 220, 150, 0.2), transparent 32%),
            radial-gradient(circle at 84% 18%, rgba(194, 236, 156, 0.36), transparent 28%),
            radial-gradient(circle at 72% 82%, rgba(10, 112, 73, 0.13), transparent 30%),
            linear-gradient(135deg, #eefdf5 0%, #d9f8ee 45%, #f7f1e8 100%);
          box-shadow: 0 32px 80px rgba(9, 112, 73, 0.1);
        }

        .sl-final-card::before {
          content: "";
          position: absolute;
          inset: 0;
          pointer-events: none;
          background:
            linear-gradient(rgba(9, 112, 73, 0.06) 1px, transparent 1px),
            linear-gradient(90deg, rgba(9, 112, 73, 0.06) 1px, transparent 1px),
            radial-gradient(circle at 92% 8%, rgba(9, 112, 73, 0.12) 1px, transparent 1px);
          background-size: 38px 38px, 38px 38px, 8px 8px;
          mask-image: linear-gradient(90deg, transparent, #000 12%, #000 88%, transparent);
        }

        .sl-final-card > * {
          position: relative;
          z-index: 1;
        }

        .sl-final-cta h2 {
          width: min(680px, 100%);
          color: var(--sl-ink);
        }

        .sl-final-cta p {
          width: min(600px, 100%);
          margin: 22px auto 26px;
          color: rgba(8, 27, 19, 0.64);
        }

        .sl-final-actions {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 12px;
        }

        .sl-btn-light {
          background: rgba(255, 255, 255, 0.78);
          color: var(--sl-ink);
          border: 1px solid rgba(8, 27, 19, 0.1);
          box-shadow: 0 14px 34px rgba(8, 27, 19, 0.08);
        }

        .sl-footer {
          background: #fff;
          padding: 58px 0 24px;
        }

        .sl-footer-inner {
          display: grid;
          grid-template-columns: minmax(220px, 0.9fr) minmax(0, 1.5fr);
          gap: 70px;
          align-items: start;
        }

        .sl-footer-brand img {
          width: 210px;
          height: auto;
          object-fit: contain;
          object-position: left center;
        }

        .sl-footer-brand p {
          margin: 18px 0 0;
          max-width: 330px;
          color: var(--sl-muted);
          font-size: 14px;
          line-height: 1.72;
        }

        .sl-footer-links {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 36px;
        }

        .sl-footer-links p {
          margin: 0 0 18px;
          color: var(--sl-muted);
          font-size: 13px;
          font-weight: 800;
        }

        .sl-footer-links a {
          display: block;
          margin-top: 13px;
          color: var(--sl-ink);
          text-decoration: none;
          font-size: 14px;
          line-height: 1.4;
          transition: color 0.18s ease;
        }

        .sl-footer-links a:hover,
        .sl-footer-bottom a:hover {
          color: var(--sl-green);
        }

                .sl-footer-address {
          padding-top: 4px;
          padding-bottom: 4px;
        }
        .sl-footer-address p {
          font-size: 12.5px;
          color: var(--sl-muted);
          margin: 0;
        }
.sl-footer-bottom {
          margin-top: 74px;
          padding-top: 22px;
          border-top: 1px solid rgba(8, 27, 19, 0.08);
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 18px;
        }

        .sl-footer-bottom p {
          margin: 0;
          color: var(--sl-muted);
          font-size: 13px;
        }

        .sl-footer-bottom div {
          display: flex;
          gap: 22px;
          flex-wrap: wrap;
        }

        .sl-footer-bottom a {
          color: var(--sl-muted);
          text-decoration: none;
          font-size: 13px;
        }

        @media (max-width: 1040px) {
          .sl-nav-links {
            gap: 16px;
          }

          .sl-industry-grid,
          .sl-price-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }

          .sl-feature-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }

          .sl-hero-content,
          .sl-industry-detail,
          .sl-ai-grid {
            grid-template-columns: 1fr;
          }

          .sl-industry-detail {
            grid-template-areas:
              "head"
              "copy"
              "features"
              "cta";
          }

          .sl-hero-copy {
            width: 100%;
          }

          .sl-template-cta {
            width: 100%;
          }
        }

        @media (max-width: 760px) {
          .sl-container,
          .sl-nav-shell {
            width: min(100% - 32px, 1180px);
          }

          .sl-nav-shell {
            height: 64px;
            justify-content: center;
          }

          .sl-menu-btn {
            display: flex;
            position: absolute;
            left: 0;
          }

          .sl-logo-link {
            position: absolute;
            left: 50%;
            transform: translateX(-50%);
          }

          .sl-logo-img {
            width: 138px;
            height: 38px;
          }

          .sl-nav-links,
          .sl-nav-actions {
            display: none;
          }

          .sl-mobile-panel {
            display: grid;
            gap: 4px;
            width: min(100% - 32px, 420px);
            margin: 0 auto 14px;
            padding: 12px;
            background: #fff;
            border: 1px solid var(--sl-line);
            border-radius: 8px;
            box-shadow: 0 18px 38px rgba(15, 23, 42, 0.14);
          }

          .sl-mobile-panel a {
            min-height: 42px;
            border-radius: 8px;
            display: flex;
            align-items: center;
            padding: 0 12px;
            color: #334155;
            font-size: 15px;
            font-weight: 800;
          }

          .sl-mobile-panel .sl-mobile-cta {
            justify-content: center;
            background: var(--sl-green);
            color: #fff;
          }

          .sl-hero {
            min-height: auto;
            padding: 104px 0 52px;
          }

          .sl-hero-content {
            gap: 30px;
          }

          .sl-hero h1 {
            font-size: clamp(33px, 9vw, 42px);
            line-height: 1.08;
          }

          .sl-hero-copy > p:not(.sl-eyebrow) {
            font-size: 16px;
            line-height: 1.64;
          }

          .sl-hero-actions {
            display: grid;
            grid-template-columns: 1fr;
            gap: 10px;
          }

          .sl-btn {
            width: 100%;
          }

          .sl-trust-line {
            align-items: flex-start;
            font-size: 13px;
          }

          .sl-chat-demo-toolbar {
            height: 40px;
          }

          .sl-video-footer {
            grid-template-columns: 1fr;
          }

          .sl-chat-demo-body {
            padding: 14px;
          }

          .sl-industry-tabs {
            justify-content: flex-start;
            width: 100%;
          }

          .sl-industry-tabs button {
            min-height: 30px;
            padding: 0 11px;
          }

          .sl-chat-phone {
            height: 390px;
            max-height: 390px;
            border-radius: 18px;
          }

          .sl-chat-messages {
            height: 252px;
            max-height: 252px;
            padding: 13px;
          }

          .sl-chat-bubble,
          .sl-typing-indicator {
            max-width: 86%;
          }

          .sl-chat-input button {
            display: none;
          }

          .sl-proof-grid {
            grid-template-columns: 1fr;
          }

          .sl-proof-item {
            min-height: 86px;
            padding: 18px;
          }

          .sl-section {
            padding: 66px 0;
          }

          .sl-section-head,
          .sl-section-head-left {
            text-align: left;
            margin-bottom: 30px;
          }

          .sl-section-head p,
          .sl-workflow-grid > div:first-child p,
          .sl-ai-copy p,
          .sl-faq-grid > div:first-child p,
          .sl-final-cta p {
            font-size: 15px;
            line-height: 1.66;
          }

          .sl-industry-grid,
          .sl-feature-grid,
          .sl-ai-card-grid,
          .sl-ai-metric-row,
          .sl-price-grid,
          .sl-workflow-grid,
          .sl-template-feature-grid,
          .sl-faq-grid {
            grid-template-columns: 1fr;
          }

          .sl-industry-media {
            height: 128px;
          }

          .sl-industry-detail {
            padding: 20px;
            gap: 20px;
          }

          .sl-industry-detail-head {
            align-items: flex-start;
          }

          .sl-template-cta {
            width: 100%;
          }

          .sl-feature-card {
            min-height: auto;
          }

          .sl-workflow-grid,
          .sl-faq-grid {
            gap: 28px;
          }

          .sl-workflow-item {
            grid-template-columns: 44px minmax(0, 1fr);
            gap: 12px;
          }

          .sl-workflow-number {
            grid-row: span 2;
            font-size: 17px;
          }

          .sl-workflow-icon {
            display: none;
          }

          .sl-price-card {
            padding: 22px;
          }

          .sl-price-head {
            min-height: auto;
          }

          .sl-price-row {
            min-height: auto;
          }

          .sl-price-value {
            font-size: clamp(30px, 9vw, 38px);
          }

          .sl-final-cta {
            padding: 58px 0 42px;
          }

          .sl-final-card {
            min-height: 300px;
            align-items: flex-start;
            text-align: left;
            padding: 36px 22px;
          }

          .sl-final-actions {
            justify-content: flex-start;
          }

          .sl-final-cta p {
            margin-left: 0;
            margin-right: 0;
          }

          .sl-footer-inner {
            grid-template-columns: 1fr;
            gap: 34px;
          }

          .sl-footer-links {
            grid-template-columns: 1fr;
            gap: 24px;
          }

          .sl-footer-brand img {
            width: 186px;
          }

          .sl-footer-bottom {
            margin-top: 42px;
            align-items: flex-start;
            flex-direction: column;
          }
        }

        @media (max-width: 380px) {
          .sl-logo-img {
            width: 124px;
            height: 34px;
          }

          .sl-hero h1 {
            font-size: 32px;
          }

          .sl-price-value {
            font-size: 30px;
          }

          .sl-chat-demo-toolbar {
            padding: 0 12px;
          }

          .sl-chat-reset {
            width: 30px;
            padding: 0;
            justify-content: center;
          }

          .sl-chat-reset svg {
            margin: 0;
          }

          .sl-chat-reset {
            font-size: 0;
          }

          .sl-chat-messages {
            height: 240px;
            max-height: 240px;
          }

          .sl-product-card {
            grid-template-columns: 1fr;
          }

          .sl-product-card img {
            width: 100%;
            height: 92px;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .stockara-landing *,
          .stockara-landing *::before,
          .stockara-landing *::after {
            scroll-behavior: auto !important;
            transition-duration: 0.01ms !important;
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .sl-header::before,
          .sl-hero::after,
          .sl-industries::before,
          .sl-features::before,
          .sl-pricing::before,
          .sl-workflow::before,
          .sl-faq::before,
          .sl-proof::before,
          .sl-ai-section::before,
          .sl-final-cta::before {
            animation: none;
          }
        }
      `}</style>
    </div>
  );
}
